<script lang="ts">
	import type { BoundaryCoordinates, Coordinates, DrawerPosition } from '$lib/components/map/types';
	import type { MainPoiCategory, PoiCategory, PoiData } from '$lib/components/pois/types';
	import type { LngLatBounds } from 'maplibre-gl';
	import type { LayoutData } from '../$types';
	import { browser } from '$app/environment';
	import { afterNavigate, beforeNavigate, goto } from '$app/navigation';
	import { page } from '$app/stores';
	import Logo from '$assets/logos/vm-logo-bib-michelin.svg';
	import LargeLogo from '$assets/logos/vm-logo-large.svg';
	import PartnerBoxMaps from '$lib/components/advertising/partners/boxmap/PartnerBoxMaps.svelte';
	import { defaultMapBoundsByCountry } from '$lib/components/map/constants';
	import {
		type ApplyPoiCategoriesChangeToSearchParamsOptions,
		createMapLayoutContext,
		getMapLayoutContext,
	} from '$lib/components/map/map-layout-context';
	import Map from '$lib/components/map/Map.svelte';
	import { setSelectedAddress } from '$lib/components/map/selectedAddress.svelte';
	import { poiCategoriesToPersist, usePersistPoiFilters } from '$lib/components/pois/filters/usePersistPoiFilters.svelte';
	import {
		isDatasheetPoiData,
		isDatasheetSideBarPoiData,
		mapPoiToCategory,
	} from '$lib/components/pois/utils';
	import SummaryDisplay from '$lib/components/summary/SummaryDisplay.svelte';
	import { ROUTE_MAP, ROUTE_MAP_POI_TEMPLATE, ROUTE_ON_THE_WAY, ROUTE_ON_THE_WAY_POI, ROUTE_RESULTS, ROUTE_SEARCH, ROUTE_SEARCH_POI, ROUTE_ZOI, ROUTE_ZOI_POI, type RoutesIds } from '$lib/constants/route';
	import { getCountryCode } from '$lib/i18n/locale';
	import { clicktripz } from '$lib/services/advertising';
	import {
		handleSearchExtensionOpening,
		searchExtensionContext,
		searchExtensionTouchpoint,
		updateSearchExtension,
	} from '$lib/services/searchExtension';
	import {
		isCorridorPageContext,
		isDatasheetPage,
		isFiltersPage,
		isMapDatasheetPage,
		isMapPageContext,
		isMapPoiListPage,
		isRouteDetailsRecommendationPage,
		isRoutePage,
		isRoutePoiListPage,
		isRoutePoiListPageContext,
		isSearchCarPage,
		isTrafficPage,
		isZoiPageContext,
		isZoiSettingsPage,
	} from '$lib/services/urls/routeIds';
	import { routesPagename } from '$lib/services/urls/routesPagename';
	import {
		getSearchParamsFiltersWithDefault,
		getSearchParamsStore,
		gotoWithParams,
		type SearchParamsType,
		type SelectedAddress,
	} from '$lib/services/urls/searchParams';
	import { isVanitySearchParams, routeWithoutVanityParams } from '$lib/services/urls/vanity';
	import { onMount, tick } from 'svelte';
	import { derived, get, writable, type Writable } from 'svelte/store';
	import { match as matchPoiCategory } from '../../params/poiCategory';

	const searchParams = getSearchParamsStore();

	let slotContainer: HTMLElement;
	let slotWrapper: HTMLElement;
	let container: HTMLElement;

	const drawerScrollPosition = writable(0);
	const panelScrollPosition = writable(0);
	export let data: LayoutData;

	$: pageData = $page.data;
	$: ({
		poisStore,
		mobileTopButtons,
		itineraryResultsStore,
		bottomsheetHeightOffset = 0,
		zoiResultsStore,
		corridorPolygonStore,
		trafficInformationStore,
		pagename,
	} = pageData);

	const containerPartialHeightPercentage = 40;

	function getPartialDrawerPosition() {
		if (!container) {
			return 0;
		}

		return (
			(container.clientHeight / 100) * containerPartialHeightPercentage - bottomsheetHeightOffset
		);
	}

	const drawerScrollPositionName = derived([drawerScrollPosition], ([$drawerScrollPosition]) => {
		if ($drawerScrollPosition === 0) {
			return 'closed';
		} else if ($drawerScrollPosition === getPartialDrawerPosition()) {
			return 'partial';
		} else if ($drawerScrollPosition >= container?.clientHeight - bottomsheetHeightOffset) {
			return 'opened';
		}
		return 'partial';
	});

	function moveDrawer(position: DrawerPosition | number, options?: { behavior?: ScrollBehavior }) {
		if (typeof position === 'number') {
			container?.scrollTo({ top: position, behavior: options?.behavior || 'instant' });
		} else if (position === 'opened') {
			slotContainer?.scrollIntoView({ block: 'start', behavior: options?.behavior || 'smooth' });
		} else if (position === 'closed') {
			container?.scrollTo({ top: 0, behavior: options?.behavior || 'smooth' });
		} else if (position === 'partial') {
			const top = getPartialDrawerPosition();
			container?.scrollTo({
				top,
				behavior: options?.behavior || 'smooth',
			});
		}
	}

	function movePanel(position: number, options?: { behavior?: ScrollBehavior }) {
		slotWrapper?.scrollTo({ top: position, behavior: options?.behavior || 'smooth' });
	}

	onMount(() => {
		$drawerScrollPosition = container.scrollTop;
		$panelScrollPosition = container.scrollTop;
	});

	/** Move the bottom sheet to different positions depending on the initial scroll position */
	function toggleDrawerScrollPosition() {
		// If the bottom sheet is already at the bottom, scroll to the middle of the container
		// If the bottom sheet content uses snapping, the bottom sheet will snap to the nearest snap
		// point
		// We use a Math.round here because the scroll position is not always an integer on mobile
		if (Math.round($drawerScrollPosition) === 0) {
			moveDrawer('partial');
		// If the bottom sheet is fully opened, scroll to the bottom of the container
		} else if ($drawerScrollPosition >= container.clientHeight - bottomsheetHeightOffset) {
			moveDrawer('closed');
		// If the bottom sheet is partially opened, move the bottom sheet to the top (fully opened)
		} else {
			moveDrawer('opened');
		}
	}

	function closeDatasheet() {
		moveDrawer('closed');
	}

	$: ({
		poisList,
		mapBounds: searchMapBounds,
		selectedAddressCenter,
		selectedPoiData,
		poiCategories,
	} = $poisStore ?? {
		poisList: [],
		mapBounds: defaultMapBoundsByCountry[getCountryCode(data.locale)],
		selectedAddressCenter: undefined,
		poiCategories: [],
		selectedPoiData: undefined,
	});

	const mapPoiCategories: Writable<MainPoiCategory[]> = writable(poiCategories || []);

	$: poisStoreFetchIsSuccess = $poisStore && !$poisStore.fetching && !$poisStore.hasError;

	$: if ($searchParams.poiCategories) {
		mapPoiCategories.set($searchParams.poiCategories);
	} else if ($page.params.category && matchPoiCategory($page.params.category)) {
		mapPoiCategories.set([$page.params.category]);
	} else if ($poisStore && poisStoreFetchIsSuccess && $poisStore.poiCategories) {
		mapPoiCategories.set($poisStore.poiCategories);
	} else {
		// on a change on $searchParams & $page.params & no poisStore.poiCategories => empty array of category
		mapPoiCategories.set([]);
	}

	const { getStoredPoiFilters } = usePersistPoiFilters();

	function applyPoiCategoriesChangeToSearchParams(options?: ApplyPoiCategoriesChangeToSearchParamsOptions) {
		$searchParams.page = 1;
		$searchParams.poiCategories = $mapPoiCategories;

		if ($poisStore?.mapBounds) {
			$searchParams.bounds = $poisStore.mapBounds;
		}

		if (options?.selectedAddress) {
			$searchParams.selectedAddress = options.selectedAddress;
		}

		const storedPoiFilters = getStoredPoiFilters();

		if (storedPoiFilters && poiCategoriesToPersist.some(category => $mapPoiCategories.includes(category))) {
			$searchParams.filters = {
				...$searchParams.filters,
				...storedPoiFilters,
			};
		}

		$searchParams.filters = {
			...$searchParams.filters,
			...getSearchParamsFiltersWithDefault($searchParams.filters, $page.data.locale, $page.data.route),
		};
	}

	function handleMapPoiCategoriesChange(updatedPoiCategories: MainPoiCategory[], options?: ApplyPoiCategoriesChangeToSearchParamsOptions) {
		mapPoiCategories.set(updatedPoiCategories);
		applyPoiCategoriesChangeToSearchParams(options);
	}

	function applySearchParamsFiltersChange(searchParamFiltersValue: SearchParamsType['filters']) {
		const poisStoreContent = poisStore && get(poisStore);
		if (poisStoreContent) {
			// add selectedAddress & poiCategoriesfrom poisStore to handle navigate from canonical url to technical url
			$searchParams.selectedAddress = poisStoreContent.selectedAddress;
			$searchParams.poiCategories = poisStoreContent.poiCategories;
			$searchParams.bounds = poisStoreContent.mapBounds;
		}
		$searchParams.filters = searchParamFiltersValue;
	}

	createMapLayoutContext({
		toggleDrawerScrollPosition,
		drawerScrollPosition,
		panelScrollPosition,
		drawerScrollPositionName,
		closeDatasheet,
		moveDrawer,
		movePanel,
		getPartialDrawerPosition,
		isTrafficPage: isTrafficPage($page.route),
		layerUrls: data.layerUrls,
		itineraryFromSearchParams: $searchParams.itinerary,
		mapPoiCategories,
		onEmitUpdateMapPoiCategoriesChange: handleMapPoiCategoriesChange,
		onAccommodationSelectDates({ startDate, endDate }) {
			applySearchParamsFiltersChange({
				...$searchParams.filters,
				ACCOMMODATION_AVAILABILITY_DATE_RANGE: {
					startDate,
					endDate,
				},
			});
		},
		onAccommodationClearDates() {
			if ($searchParams.filters?.ACCOMMODATION_AVAILABILITY_DATE_RANGE) {
				applySearchParamsFiltersChange({
					...$searchParams.filters,
					ACCOMMODATION_AVAILABILITY_DATE_RANGE: undefined,
				});
			}
		},
		onMichelinSelectionChange(isOnlyMichelin) {
			const searchFilters = { ...($searchParams.filters ?? {}) };

			// TODO: Refactor this to set the filters in a more generic way { [poiCategoryId]: { [poiFilterId]: value } }
			if (isOnlyMichelin) {
				searchFilters.MICHELIN_ACCOMMODATION = isOnlyMichelin;
				searchFilters.ONLY_MICHELIN_GUIDE_SELECTION = isOnlyMichelin;
			} else {
				delete searchFilters.MICHELIN_ACCOMMODATION;
				delete searchFilters.ONLY_MICHELIN_GUIDE_SELECTION;
			}

			applySearchParamsFiltersChange(searchFilters);
		},
	});

	const mapLayoutContext = getMapLayoutContext();
	const {
		mapCenter,
		isSummaryOpen,
		trafficEventSummary,
		clickedPosition,
		itineraryStore,
		isPinMarkerOnMapWithSummary,
		isMapFullscreen,
	} = mapLayoutContext;
	const { itinerary } = itineraryStore;

	function hasVanityParams(params: Record<string, string> | null) {
		// has at least one known vanity param excluding the poi param
		return (
			params && !params.poi && ['category', 'address', 'addresses'].some(param => !!params[param])
		);
	}

	// Search extension update mechanism
	$: searchExtensionContext.updateFromPoisStore($poisStore);
	$: searchExtensionContext.updateFromItineraryStore($itinerary);
	$: if (browser) updateSearchExtension($searchExtensionContext);

	beforeNavigate((navigation) => {
		handleSearchExtensionOpening(navigation, $searchExtensionContext);

		if (
			navigation.from
			&& navigation.to?.route?.id
			&& !isVanitySearchParams(navigation.to.url)
			&& hasVanityParams(navigation.to.params)
		) {
			navigation.cancel();
			goto(routeWithoutVanityParams(navigation.to));
		}
	});

	// Scroll back to top when navigating
	// Only if this is not the first navigation
	afterNavigate(({ type, to }) => {
		if (type !== 'enter' && to) {
			if (
				isMapPoiListPage(to.route)
				|| isRoutePoiListPage(to.route)
				|| isMapDatasheetPage(to.route)
				|| isRoutePage(to.route)
			) {
				// On desktop, we want the slot container to be scrolled to the top
				if (window.matchMedia('(min-width: 960px)').matches) {
					slotWrapper.scrollTo({ top: 0 });
				} else {
					slotContainer.scrollTo({ top: 0 });
				}

				// On mobile, we want the slot container to be scrolled to the top
				// only if the drawer is not closed
				if ($drawerScrollPosition >= container.clientHeight) {
					moveDrawer('opened');
				}
			}

			if (isDatasheetPage(to.route) && window.matchMedia('(max-width: 959px)').matches) {
				moveDrawer(272, { behavior: 'smooth' });
			}
		}
	});

	$: hasBigDatasheetPanel
		= isDatasheetPage($page.route) && isDatasheetSideBarPoiData(selectedPoiData);

	$: sizeDatasheetPanel = hasBigDatasheetPanel ? 'big-datasheet-panel' : '';

	function handleSearchBoundsChange(
		mapBounds: BoundaryCoordinates,
		center?: Coordinates,
		formattedAddress?: SelectedAddress,
		poiCat?: MainPoiCategory[],
	) {
		if (isRouteDetailsRecommendationPage($page.route) || isZoiPageContext($page.route)) {
			return gotoWithParams(ROUTE_SEARCH, {
				...$searchParams,
				page: 1,
				bounds: mapBounds,
				poiCategories: poiCat ?? $mapPoiCategories,
				center,
				selectedPoi: undefined,
			});
		}

		$searchParams.page = 1;
		$searchParams.bounds = mapBounds;
		$searchParams.poiCategories = poiCat ?? $mapPoiCategories;

		if (center) {
			$searchParams.center = center;
		}

		if (formattedAddress) {
			$searchParams.selectedAddress = formattedAddress;
		} else {
			if ($searchParams.selectedAddress) {
				// Reset the entity type and coordinates when a reload bounds is made
				$searchParams.selectedAddress.boundsSync = false;
				$searchParams.selectedAddress.address = '';
			}
		}
	}

	function handlePoiUnselect() {
		const newSearchParams = {
			...$searchParams,
			selectedPoi: undefined,
			poiCategories: $mapPoiCategories,
			bounds: searchMapBounds,
		};
		if (isRoutePoiListPageContext($page.route)) {
			gotoWithParams(ROUTE_SEARCH, newSearchParams);
			return;
		}
		if (isZoiPageContext($page.route)) {
			gotoWithParams(ROUTE_ZOI, newSearchParams);
			return;
		}
		if (isCorridorPageContext($page.route)) {
			gotoWithParams(ROUTE_ON_THE_WAY, newSearchParams);
			return;
		}
		gotoWithParams('/map', newSearchParams);
	}

	function getSelectedPoiRedirecParams(
		poi: PoiData,
		selectedPoi: { id: string; type: PoiCategory },
		isDatasheet: boolean,
		searchParams: SearchParamsType,
	): {
		route: RoutesIds;
		searchParams: SearchParamsType;
	} {
		switch (true) {
			case isRoutePoiListPageContext($page.route):
				return {
					route: isDatasheet ? ROUTE_SEARCH_POI : ROUTE_SEARCH,
					searchParams,
				};
			case isZoiPageContext($page.route):
				return {
					route: isDatasheet ? ROUTE_ZOI_POI : ROUTE_ZOI,
					searchParams,
				};
			case isCorridorPageContext($page.route):
				return {
					route: isDatasheet ? ROUTE_ON_THE_WAY_POI : ROUTE_ON_THE_WAY,
					searchParams,
				};

			default:
				return {
					route: isDatasheet ? ROUTE_MAP_POI_TEMPLATE.replace(':category', selectedPoi.type).replace(':urlIdentifier', poi.urlIdentifier) as RoutesIds : ROUTE_MAP,
					searchParams: isDatasheet
						? {
							...searchParams,
							selectedPoi: undefined,
						}
						: searchParams,
				};
		}
	}

	function handleSelectedPoiChange(poi: PoiData | null) {
		if (!poi) {
			return handlePoiUnselect();
		}

		const selectedPoi = { id: poi.id, type: mapPoiToCategory(poi) };
		const newSearchParams = {
			...$searchParams,
			poiCategories: $mapPoiCategories,
			bounds: searchMapBounds,
			selectedPoi,
		};

		if (isDatasheetPoiData(poi)) {
			$isSummaryOpen = false;
		}

		const { route, searchParams: searchParamsFromRedirectParams } = getSelectedPoiRedirecParams(
			poi,
			selectedPoi,
			isDatasheetPoiData(poi),
			newSearchParams,
		);

		searchExtensionTouchpoint.activate('poimap_click');
		gotoWithParams(route, searchParamsFromRedirectParams);
	}

	function onMapBoundsChange(updatedMapBounds: LngLatBounds) {
		$mapCenter = updatedMapBounds.getCenter();
	}

	function cancelPosition(clearSelectedAddress: boolean = false) {
		if (clearSelectedAddress) {
			$searchParams.selectedAddress = undefined;
			$searchParams.center = undefined;
			setSelectedAddress(null);
		}

		$isPinMarkerOnMapWithSummary = false;
		$isSummaryOpen = false;
		$clickedPosition = undefined;
	}

	function onClickBackHome() {
		cancelPosition();
		gotoWithParams(`/map`, $searchParams);
	}

	let contentRect: DOMRectReadOnly;
	$: shouldDisplaySummary = Boolean($clickedPosition) || $isPinMarkerOnMapWithSummary;
	$: if (isMapDatasheetPage($page.route)) {
		$clickedPosition = undefined;
		$isPinMarkerOnMapWithSummary = false;
		selectedAddressCenter = undefined;
	}

	// Clicktripz arrival handling
	$: if (browser) {
		if (pagename === routesPagename.ITINERARY_SEARCH) {
			clicktripz.disable();
		} else {
			clicktripz.enable();
		}
	}
	$: if (browser && (poisStore || itineraryResultsStore)) {
		let ctzArrivalCity: string | undefined;
		let ctzArrivalCountryCode: string | undefined;

		if (itineraryResultsStore && $itineraryResultsStore?.arrivalAddress) {
			// From itinerary
			const { arrivalAddress } = $itineraryResultsStore;
			ctzArrivalCity = arrivalAddress.city || undefined;
			ctzArrivalCountryCode = arrivalAddress.country?.code;
		} else if (poisStore && $poisStore) {
			if ($poisStore.selectedPoiData?.address) {
				// From selected poi
				const { address } = $poisStore.selectedPoiData;
				ctzArrivalCity = address?.city || undefined;
				ctzArrivalCountryCode = address.country?.code;
			} else if ($poisStore.selectedAddress) {
				// From map result
				const { selectedAddress } = $poisStore;
				ctzArrivalCity = selectedAddress.city;
				ctzArrivalCountryCode = selectedAddress.countryCode;
			}
		}

		if (ctzArrivalCity && ctzArrivalCountryCode) {
			clicktripz.setArrival({ city: ctzArrivalCity, countryCode: ctzArrivalCountryCode });
		}
	}
</script>

<div
	class="main-container bg-neutral-weak {sizeDatasheetPanel}"
	class:filters={isFiltersPage($page.route)}
	class:search-car={isSearchCarPage($page.route)}
	class:settings={isZoiSettingsPage($page.route)}
	class:homepage={isMapPageContext($page.route)}
	class:routes={isRoutePage($page.route)}
	class:fullscreen={$isMapFullscreen}
	bind:this={container}
	on:scroll={() => {
		// NOTE: bottomsheet scroll position is updated here
		$drawerScrollPosition = container.scrollTop;
		$panelScrollPosition = container.scrollTop;
	}}
	style="--bottomsheet-height-offset: -{bottomsheetHeightOffset}px;"
>
	<div class="map" bind:contentRect>
		<Map
			{shouldDisplaySummary}
			isTrafficPage={isTrafficPage($page.route)}
			mapBounds={searchMapBounds}
			class="md:relative"
			{selectedAddressCenter}
			selectedPoiCategories={mapPoiCategories}
			onPoiCategoriesChange={poiCategories => handleMapPoiCategoriesChange(poiCategories)}
			poiData={poisList}
			{selectedPoiData}
			onSelectedPoiChange={(poiData) => {
				if (poiData) {
					const isParkingOrGasStationPoi = ['ParkingPOI', 'ServiceGasStationPOI'].includes(
						poiData.__typename,
					);
					if (!isParkingOrGasStationPoi) {
						$isMapFullscreen = false;
					}

					if (isParkingOrGasStationPoi) {
						shouldDisplaySummary = true;
					}
				}

				handleSelectedPoiChange(poiData);
			}}
			onMapBoundsChange={mapDetails => onMapBoundsChange(mapDetails)}
			onSearchBoundsChange={({ center, mapBounds, formattedAddress, selectedPois }) => {
				handleSearchBoundsChange(mapBounds, center, formattedAddress, selectedPois);
			}}
			onBackHome={onClickBackHome}
			onBackOverview={() => {
				gotoWithParams(ROUTE_RESULTS, { ...$searchParams });
			}}
			onBackToRouteResults={() =>
				gotoWithParams(ROUTE_RESULTS, { ...$searchParams, itinerary: $itinerary })}
			onBackOnTheWay={() =>
				gotoWithParams(ROUTE_ON_THE_WAY, { ...$searchParams, selectedPoi: undefined })}
			zois={$zoiResultsStore?.zois ?? []}
			{mobileTopButtons}
			routes={$itineraryResultsStore?.routes ?? []}
			corridor={$corridorPolygonStore}
			trafficEvents={$trafficInformationStore?.listForMap?.items ?? []}
			enableToolsPartnerPoiCategory={$page.data.hasPartnerAdinmap
				&& !isZoiPageContext($page.route)
				&& !isCorridorPageContext($page.route)}
			mapWidth={contentRect?.width}
		/>

		{#if !$isMapFullscreen}
			<img
				src={Logo}
				alt="Michelin"
				class="absolute left-4 top-6 md:block hidden z-hover"
				height="24"
				width="120"
			/>
		{/if}
		<img
			height="32"
			width="126"
			src={LargeLogo}
			alt="ViaMichelin"
			class="absolute-center-x top-2 md:hidden z-hover"
		/>
	</div>

	{#if shouldDisplaySummary}
		<div
			class="summary-container pr-4 pl-2 absolute left-0 right-0 bottom-0 mb-4 mx-auto max-w-150 w-full"
		>
			<SummaryDisplay
				poiData={selectedPoiData}
				trafficEventData={$trafficEventSummary ?? undefined}
				on:summaryVisible={async (e) => {
					$isSummaryOpen = e.detail;
					if (!$isSummaryOpen) {
						await tick();
						moveDrawer('partial', { behavior: 'instant' });
					}
				}}
				on:cancelPosition={({ detail: { cancelAction } }) => cancelPosition(cancelAction)}
				on:closeTrafficSummary={() => ($trafficEventSummary = null)}
			/>
		</div>
	{/if}

	<div class="slot-container md:p-2 md:pr-0 md:h-dvh printable" bind:this={slotContainer}>
		<div
			class="md:overflow-hidden md:rd-2 md:h-dvh max-h-full printable"
			class:md:shadow-smooth-block={!isDatasheetPage($page.route)}
		>
			<div
				class="slot-wrapper md:bg-neutral-weakest h-full scrollbar-thin printable"
				class:md:bg-neutral-weakest={!isMapDatasheetPage($page.route)}
				bind:this={slotWrapper}
				on:scroll={() => {
					// NOTE: panel scroll position is updated here
					$panelScrollPosition = slotWrapper.scrollTop;
				}}
			>
				{#if browser && !$isSummaryOpen && Math.round($drawerScrollPosition) !== 0}
					<div
						class="absolute top--2 md:hidden right-2 -translate-y-[100%] {isMapPoiListPage(
							$page.route,
						)
							? 'top--6 md:top--2'
							: ''}"
					>
						<PartnerBoxMaps />
					</div>
				{/if}
				<slot />
			</div>
		</div>
	</div>
</div>

<style>
	.main-container {
		height: 100%;
		overflow-y: scroll;
		scroll-snap-type: y mandatory;
		scroll-behavior: smooth;
	}

	.map {
		position: sticky;
		top: 0;
		width: 100%;
		height: 100%;
	}

	.slot-container {
		position: relative;
		width: 100%;
		margin-top: var(--bottomsheet-height-offset);
	}

	/* TODO: Replace with uno screen directive */
	@media screen and (min-width: 960px) {
		.main-container {
			position: relative;
			display: grid;
			grid-template-areas: "slotcontainer map";
			grid-template-columns: var(--slot-container-col, 508px) 1fr;
			gap: 8px;
			overflow: hidden;
			height: 100%;
			width: 100%;
			scroll-snap-type: none;
			transition: grid-template-columns 450ms cubic-bezier(0.23, 1, 0.32, 1) 60ms;
		}

		.main-container.fullscreen {
			--slot-container-col: 0px;
			gap: 0;
		}

		.main-container.fullscreen .slot-container {
			transition: transform 350ms cubic-bezier(0.23, 1, 0.32, 1) 20ms;
			transform: translate3d(-100%, 0, 0);
		}
		.main-container.fullscreen .map {
			border-radius: 0;
			padding: 0;
			transition: padding 200ms ease-out 20ms;
		}
		.main-container.fullscreen .map :global(> .maplibregl-map) {
			border-radius: 0;
		}

		.main-container.big-datasheet-panel {
			/* 824 corresponding to the datasheet content + the cards recommendation with all margins and paddings */
			--slot-container-col: 824px;
		}

		.map {
			position: relative;
			grid-area: map;
			height: 100%;
			width: 100%;
			/* So that the map copyright does not interfere with everything else */
			isolation: isolate;
			padding: 8px 8px 8px 0;
			transition: padding 150ms ease-in;
		}

		.map :global(> .maplibregl-map) {
			border-radius: 0.5rem; /* Ensure the map is rounded even after the map was fullscreen */
		}

		.summary-container {
			grid-area: map;
		}

		.slot-container {
			display: block;
			width: 100%;
			top: 0;
			grid-area: slotcontainer;
			margin-top: 0;
			transform: translate3d(0, 0, 0);
			transition: transform 350ms cubic-bezier(0.23, 1, 0.32, 1) 20ms;
		}

		.slot-wrapper {
			overflow-x: auto;
			/* Make sure the scroll bar is always visible so the panel content does not resize */
			overflow-y: scroll;
		}
	}

	@media print {
		.main-container,
		.slot-container {
			overflow: visible !important;
			height: 99% !important;
		}
	}
</style>
